import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Bus, Navigation, CheckCircle2, Home, UserX, RefreshCw, MapPin, Radio,
  Play, Square, Phone, User,
} from "lucide-react";
import { toast } from "sonner";

function statusPill(s, t) {
  if (s === "delivered") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"><Home className="w-3 h-3" />{t("delivered") || "تم التسليم"}</span>;
  if (s === "boarded") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/15 text-primary"><CheckCircle2 className="w-3 h-3" />{t("boarded") || "صعد"}</span>;
  if (s === "absent") return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/15 text-destructive"><UserX className="w-3 h-3" />{t("absent") || "غائب"}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{t("waiting") || "بانتظار"}</span>;
}

export default function MyRoutePage() {
  const { t } = useI18n();
  const [data, setData] = useState({ trips: [], driver: null });
  const [busy, setBusy] = useState({});
  const [broadcasting, setBroadcasting] = useState(false);
  const [lastFix, setLastFix] = useState(null);
  const watchId = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get("/drivers/me/trip-today");
      setData(r);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // GPS broadcast — auto-starts when driver hits "Start Trip"
  const startBroadcast = () => {
    if (!navigator.geolocation) { toast.error(t("geolocation_not_supported") || "GPS not supported"); return; }
    if (watchId.current) return;
    watchId.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude, accuracy, speed, heading } = pos.coords;
        setLastFix({ lat: latitude, lng: longitude, at: new Date(), speed });
        try { await api.post("/drivers/me/location", { lat: latitude, lng: longitude, accuracy, speed, heading }); }
        catch (e) { console.warn("driver location broadcast failed:", e?.message || e); }
      },
      (err) => { toast.error(err.message || "Geolocation error"); stopBroadcast(); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 },
    );
    setBroadcasting(true);
    toast.success(t("gps_started") || "GPS يعمل الآن");
  };
  const stopBroadcast = () => {
    if (watchId.current) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; }
    setBroadcasting(false);
  };
  useEffect(() => () => stopBroadcast(), []);

  const startTrip = async (trip) => {
    setBusy({ ...busy, [trip.id]: true });
    try {
      const { data: r } = await api.post(`/trips/${trip.id}/broadcast-start`);
      toast.success(t("trip_started_notified") || `الرحلة بدأت — تنبيه ${r.notified_parents} ولي أمر`);
      startBroadcast();
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy({ ...busy, [trip.id]: false });
  };

  const endTrip = async (trip) => {
    if (!window.confirm(t("confirm_end_trip") || "هل انتهت الرحلة؟")) return;
    setBusy({ ...busy, [trip.id]: true });
    try {
      await api.put(`/trips/${trip.id}/status`, { status: "ended" });
      stopBroadcast();
      toast.success(t("trip_ended") || "انتهت الرحلة");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy({ ...busy, [trip.id]: false });
  };

  const markStudent = async (trip, student, status) => {
    const key = `${trip.id}-${student.id}`;
    setBusy({ ...busy, [key]: true });
    try {
      await api.post(`/trips/${trip.id}/attendance/${student.id}`, { status, notify_parent: true });
      toast.success(t("saved") || "تم");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy({ ...busy, [key]: false });
  };

  if (!data.trips?.length) {
    return (
      <div className="p-6">
        <Card className="p-10 text-center border-dashed">
          <Bus className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <div className="text-lg font-medium">{t("no_trip_today") || "لا توجد رحلة مسندة اليوم"}</div>
          <div className="text-sm text-muted-foreground mt-1">{t("check_with_admin") || "راجع الإدارة إذا كان يجب أن تكون هناك رحلة."}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <h1 className="text-2xl md:text-3xl font-semibold flex items-center gap-2">
          <Bus className="w-6 h-6" /> {t("my_route_today") || "رحلاتي اليوم"}
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-trips">
            <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("refresh") || "تحديث"}
          </Button>
          {broadcasting && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 animate-pulse">
              <Radio className="w-3 h-3" /> GPS
            </span>
          )}
        </div>
      </div>

      {data.trips.map((trip) => (
        <Card key={trip.id} className="p-4 md:p-6 border shadow-none" data-testid={`trip-${trip.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-lg font-semibold flex items-center gap-2">
                <Navigation className="w-4 h-4 text-primary" />
                {trip.route_name || trip.route_id?.slice(0, 8)} — {(trip.direction === "to_school" || trip.direction === "morning") ? (t("dir_to_school") || "إلى المدرسة") : (t("dir_from_school") || "من المدرسة")}
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">{trip.date} · {trip.status || "pending"}</div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm bg-primary/10 text-primary px-3 py-1.5 rounded-lg font-medium" data-testid={`counter-${trip.id}`}>
                {t("delivered_progress") || "تم تسليم"} {trip.delivered_count} / {trip.total_count}
              </div>
              {trip.status !== "started" && trip.status !== "ended" && (
                <Button onClick={() => startTrip(trip)} disabled={busy[trip.id]} data-testid={`start-${trip.id}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Play className="w-4 h-4 me-1" /> {t("start_trip") || "بدء الرحلة"}
                </Button>
              )}
              {trip.status === "started" && (
                <Button onClick={() => endTrip(trip)} disabled={busy[trip.id]} data-testid={`end-${trip.id}`}
                        variant="destructive">
                  <Square className="w-4 h-4 me-1" /> {t("end_trip") || "إنهاء الرحلة"}
                </Button>
              )}
            </div>
          </div>

          {trip.students?.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8 border border-dashed rounded-lg">
              {t("no_students_on_route") || "لا يوجد طلاب مسندون لهذا المسار"}
            </div>
          ) : (
            <div className="space-y-2">
              {trip.students.map((s) => {
                const key = `${trip.id}-${s.id}`;
                const done = s.status === "delivered";
                const absent = s.status === "absent";
                return (
                  <Card key={s.id}
                        className={`p-3 border shadow-none flex flex-wrap items-center gap-3 ${done ? "bg-emerald-50/60 dark:bg-emerald-950/20" : absent ? "bg-destructive/5" : ""}`}
                        data-testid={`stu-row-${s.id}`}>
                    <div className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center shrink-0">
                      {s.photo_id ? (
                        <img src={`/api/files/${s.photo_id}`} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : <User className="w-5 h-5 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                        {s.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.address}</span>}
                        {s.parent_phone && (
                          <a href={`tel:${s.parent_phone}`} className="inline-flex items-center gap-1 hover:text-primary smooth" data-testid={`call-${s.id}`}>
                            <Phone className="w-3 h-3" />{s.parent_phone}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusPill(s.status, t)}
                      {!done && !absent && (
                        <>
                          <Button size="sm" onClick={() => markStudent(trip, s, "delivered")}
                                  disabled={busy[key]} data-testid={`deliver-${s.id}`}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <CheckCircle2 className="w-4 h-4 me-1" /> {t("mark_delivered") || "تم التسليم"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => markStudent(trip, s, "absent")}
                                  disabled={busy[key]} data-testid={`absent-${s.id}`}>
                            <UserX className="w-4 h-4 me-1" /> {t("mark_absent") || "غائب"}
                          </Button>
                        </>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {lastFix && broadcasting && (
            <div className="mt-3 text-xs text-muted-foreground font-mono">
              📍 {lastFix.lat.toFixed(5)}, {lastFix.lng.toFixed(5)} · {new Date(lastFix.at).toLocaleTimeString()}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
